import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadPageImage, slugify } from '@/lib/customPageApi';
import type { Block, CustomPage } from '@/types/customPage';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

function defaultBlock(type: Block['type']): Block {
  switch (type) {
    case 'heading':
      return { type: 'heading', text: 'New heading', size: 'lg' };
    case 'paragraph':
      return { type: 'paragraph', text: '' };
    case 'image':
      return { type: 'image', url: '', alt: '' };
    case 'button':
      return { type: 'button', label: 'Click here', href: '/' };
  }
}

export default function AdminCustomPageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const [page, setPage] = useState<CustomPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [seoDescription, setSeoDescription] = useState('');
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingImageIndexRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data } = await supabase
      .from('rxk_custom_pages')
      .select('id, slug, title, status, blocks, seo_description')
      .eq('id', id)
      .maybeSingle();
    const p = data as CustomPage | null;
    setPage(p);
    if (p) {
      setTitle(p.title);
      setSlug(p.slug);
      setStatus(p.status);
      setSeoDescription(p.seo_description ?? '');
      setBlocks(p.blocks ?? []);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const updateBlock = (index: number, patch: Partial<Block>) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? ({ ...b, ...patch } as Block) : b)));
  };

  const removeBlock = (index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const moveBlock = (index: number, dir: -1 | 1) => {
    setBlocks((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addBlock = (type: Block['type']) => {
    setBlocks((prev) => [...prev, defaultBlock(type)]);
  };

  const triggerImageUpload = (index: number) => {
    pendingImageIndexRef.current = index;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const index = pendingImageIndexRef.current;
    e.target.value = '';
    if (!file || index === null) return;
    setUploadingIndex(index);
    try {
      const url = await uploadPageImage(file);
      updateBlock(index, { url } as Partial<Block>);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    if (!slug.trim()) {
      push('error', 'Slug is required');
      return;
    }
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('rxk_custom_pages')
        .update({
          title,
          slug: slugify(slug),
          status,
          blocks,
          seo_description: seoDescription || null,
          updated_by: user?.id ?? null,
        })
        .eq('id', id);
      if (error) throw error;
      push('success', status === 'published' ? 'Page saved and live' : 'Draft saved');
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const { error } = await supabase.from('rxk_custom_pages').delete().eq('id', id);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Page deleted');
    navigate('/admin/pages');
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!page) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Page not found" />
      </AppShell>
    );
  }

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/pages')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-[20px] font-bold text-ink flex-1 truncate">{title || 'Untitled Page'}</h1>
          <Badge tone={status === 'published' ? 'success' : 'neutral'}>{status}</Badge>
          {status === 'published' && (
            <a href={`/${page.slug}`} target="_blank" rel="noopener noreferrer" className="text-ink-faint hover:text-ink">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <InlineConfirm label="Delete" confirmLabel="Confirm delete" onConfirm={handleDelete} />
        </div>

        <Card>
          <CardHeader title="Page Settings" />
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Title">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field label="URL slug (renders at /slug)">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="my-page" />
              </Field>
            </div>
            <Field label="SEO description (optional)">
              <Textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} rows={2} />
            </Field>
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'published')} className="!w-48">
                <option value="draft">Draft (not visible)</option>
                <option value="published">Published (live)</option>
              </Select>
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Blocks"
            subtitle={`${blocks.length} block(s)`}
            action={
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="secondary" onClick={() => addBlock('heading')}>
                  <Plus className="w-3 h-3" /> Heading
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('paragraph')}>
                  <Plus className="w-3 h-3" /> Paragraph
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('image')}>
                  <Plus className="w-3 h-3" /> Image
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('button')}>
                  <Plus className="w-3 h-3" /> Button
                </Button>
              </div>
            }
          />
          <CardBody className="space-y-3">
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleFileChange} />
            {blocks.length === 0 ? (
              <EmptyState title="No blocks yet — add one above" />
            ) : (
              blocks.map((block, index) => (
                <div key={index} className="border border-border-subtle rounded-lg p-3 flex gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge tone="neutral">{block.type}</Badge>
                    </div>
                    {block.type === 'heading' && (
                      <div className="flex gap-2">
                        <Input value={block.text} onChange={(e) => updateBlock(index, { text: e.target.value })} className="flex-1" />
                        <Select value={block.size} onChange={(e) => updateBlock(index, { size: e.target.value as 'lg' | 'xl' })} className="!w-24">
                          <option value="lg">Large</option>
                          <option value="xl">X-Large</option>
                        </Select>
                      </div>
                    )}
                    {block.type === 'paragraph' && (
                      <Textarea value={block.text} onChange={(e) => updateBlock(index, { text: e.target.value })} rows={3} />
                    )}
                    {block.type === 'image' && (
                      <div className="space-y-2">
                        {block.url && <img src={block.url} alt={block.alt} className="h-24 rounded-lg object-cover" />}
                        <Button size="sm" variant="secondary" onClick={() => triggerImageUpload(index)} disabled={uploadingIndex === index}>
                          <Upload className="w-3.5 h-3.5" /> {uploadingIndex === index ? 'Uploading…' : block.url ? 'Replace image' : 'Upload image'}
                        </Button>
                        <p className="text-[10.5px] text-ink-faint">Full-width, not cropped — landscape or portrait both work, up to ~1600px wide</p>
                        <Input value={block.alt} onChange={(e) => updateBlock(index, { alt: e.target.value })} placeholder="Alt text" />
                        <Input value={block.caption ?? ''} onChange={(e) => updateBlock(index, { caption: e.target.value })} placeholder="Caption (optional)" />
                      </div>
                    )}
                    {block.type === 'button' && (
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={block.label} onChange={(e) => updateBlock(index, { label: e.target.value })} placeholder="Button label" />
                        <Input value={block.href} onChange={(e) => updateBlock(index, { href: e.target.value })} placeholder="Link (e.g. /products)" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveBlock(index, -1)} disabled={index === 0} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint disabled:opacity-30 cursor-pointer">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint disabled:opacity-30 cursor-pointer">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeBlock(index)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        <Button onClick={handleSave} loading={saving}>
          Save
        </Button>
      </div>
    </AppShell>
  );
}
